/**
 * M24 — AI 생성 텍스트(최종검토 issues/checks, 수정근거, 파이프라인 소견)의
 * 영어 변수토큰을 현재 UI 언어 라벨로 치환하는 표시-시점 유틸.
 *
 * - 신규 생성 텍스트는 백엔드 프롬프트가 이미 해당 언어로 만들지만,
 *   과거 저장분/파이프라인 소견(review_notes)은 영어 변수명이 남아 있을 수 있다.
 * - 이 유틸을 렌더 직전에 적용하면 "보는 사람의 언어"로 라이브 치환된다.
 *
 * 백엔드 ``app/services/field_terms.py`` 의 토큰과 동기 유지.
 */

type Lang = "ko" | "ja";

function normLang(lang: string | undefined): Lang {
  return lang?.toLowerCase().startsWith("ja") ? "ja" : "ko";
}

/** 영어 변수토큰 → {ko, ja} 라벨. (fieldLabels 및 amount 토큰) */
const TOKEN_TERMS: Record<string, { ko: string; ja: string }> = {
  document_id: { ko: "PDF 파일명", ja: "PDF名" },
  local_agent_code: { ko: "현지 대리인 코드", ja: "現地代理人コード" },
  billing_party: { ko: "청구처", ja: "請求先" },
  swift_code: { ko: "SWIFT 코드", ja: "SWIFTコード" },
  agent_name: { ko: "대리인명", ja: "代理人名" },
  agent_invoice_number: { ko: "대리인 인보이스 번호", ja: "代理人インボイスNo" },
  invoice_date: { ko: "인보이스 발행일", ja: "インボイス発行日" },
  total_amount: { ko: "청구 금액(합계)", ja: "請求金額（合計）" },
  withholding_tax: { ko: "원천징수세액", ja: "源泉所得税額" },
  ref_number: { ko: "TTDC 케이스 번호", ja: "TTDC案件番号" },
  final_payable_amount: { ko: "최종 지불 금액", ja: "実際請求金額" },
  agent_currency: { ko: "통화", ja: "通貨" },
  agent_id: { ko: "대리인 ID", ja: "代理人ID" },
  is_ttdc_text: { ko: "TTDC 표기", ja: "TTDC表記" },
  billed: { ko: "청구액", ja: "請求額" },
  payable: { ko: "지불액", ja: "支払額" },
  withholding: { ko: "원천징수액", ja: "源泉徴収額" },
  gross: { ko: "총액", ja: "総額" },
  net: { ko: "실지급액", ja: "実支給額" },
  calc: { ko: "계산값", ja: "計算値" },
  diff: { ko: "차이", ja: "差異" },
};

// 긴 토큰 우선(부분매칭 방지). 식별자 경계(영숫자/밑줄 제외)로 감싼다.
const TOKENS_SORTED = Object.keys(TOKEN_TERMS).sort((a, b) => b.length - a.length);

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

const TOKEN_REGEX = new RegExp(
  `(?<![A-Za-z0-9_])(${TOKENS_SORTED.map(escapeRegExp).join("|")})(?![A-Za-z0-9_])`,
  "g",
);

/** AI 텍스트 한 줄의 영어 변수토큰을 현재 언어 라벨로 치환. */
export function localizeAiText(text: string, lang: string | undefined): string {
  if (!text) return text;
  const l = normLang(lang);
  return text.replace(TOKEN_REGEX, (m) => TOKEN_TERMS[m]?.[l] ?? m);
}
